import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { Customer, customerConfig, InitCustomerConfig } from '@uxdf/direct-shared-lib';
import * as CoreActions from '@uxdf/direct-shared-lib/src/lib/login/core.actions';
import { BackendLoyaltyService } from '../services/backend-loyalty.service';
import * as LoyaltyTileActions from '../actions/actions-loyalty-tile';
import { LoyaltyTileHelperService } from '../services/loyalty-tile-helper.service';

@Injectable()
export class EffectsLoyaltyTile {
  // @loyaltyDocs: send http request for access to loyalty
  request$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoyaltyTileActions.loyaltyTileRequest),
      withLatestFrom(this.store.pipe(select(customerConfig))),
      switchMap(([, customerConfigState]) => {
        const config: Customer = customerConfigState.customerConfig;

        return this.backendLoyaltyService
          .requestToHierarchy(config.central_account_number, config.default_store_code)
          .pipe(
            map((data: boolean) => LoyaltyTileActions.loyaltyTileRequestSuccess({ isRequested: data })),
            catchError(error => {
              if (error && error.error !== undefined && (error.error.code === 40 || error.error.code === 42)) {
                return of(new CoreActions.DirectUserRoleHasBeenChanged());
              } else {
                return of(LoyaltyTileActions.loyaltyTileRequestError({ error: error.error.message }));
              }
            })
          );
      })
    )
  );

  requestSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LoyaltyTileActions.loyaltyTileRequestSuccess),
        map(() => {
          this.loyaltyTileService.setRequestSent();
          this.loyaltyTileService.alertRequestSuccess();
        })
      ),
    { dispatch: false }
  );

  requestError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LoyaltyTileActions.loyaltyTileRequestError),
        map((action: any) => action.payload),
        map((message: string) => {
          this.loyaltyTileService.alertError('Loyalty error: ' + message);
        })
      ),
    { dispatch: false }
  );

  // @loyaltyDocs: send http request for approve access to loyalty
  approve$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoyaltyTileActions.loyaltyTileApprove),
      withLatestFrom(this.store.pipe(select(customerConfig))),
      switchMap(([, customerConfigState]) => {
        const config: Customer = customerConfigState.customerConfig;

        return this.backendLoyaltyService
          .approveForHierarchy(config.central_account_number, config.default_store_code)
          .pipe(
            map(() => LoyaltyTileActions.loyaltyTileApproveSuccess()),
            catchError(error => {
              if (error && error.error !== undefined && (error.error.code === 40 || error.error.code === 42)) {
                return of(new CoreActions.DirectUserRoleHasBeenChanged());
              } else {
                return of(LoyaltyTileActions.loyaltyTileApproveError({ error: error.error.message }));
              }
            })
          );
      })
    )
  );

  approveSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoyaltyTileActions.loyaltyTileApproveSuccess),
      map(() => {
        this.loyaltyTileService.alertApproveSuccess();
        return new InitCustomerConfig();
      })
    )
  );

  approveError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LoyaltyTileActions.loyaltyTileApproveError),
        map(({ error }) => {
          this.loyaltyTileService.alertError('Loyalty error: ' + error);
        })
      ),
    { dispatch: false }
  );

  constructor(
    private readonly actions$: Actions,
    private readonly backendLoyaltyService: BackendLoyaltyService,
    private readonly store: Store<any>,
    private readonly loyaltyTileService: LoyaltyTileHelperService
  ) {}
}
